import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Menu,
  MenuItem,
  useTheme,
  useMediaQuery,
  ButtonBase,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  Business as BusinessIcon,
  BarChart as BarChartIcon,
  Settings as SettingsIcon,
  PersonOutline as PersonIcon,
  Logout as LogoutIcon,
} from '@mui/icons-material';
import Logo from "./Logo";
import { brand } from "../theme";

const drawerWidth = 260;

export default function AdminLayout({ children, user, onLogout }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);

  const navigate = useNavigate();
  const location = useLocation();

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    handleMenuClose();
    onLogout();
  };

  const adminMenuItems = [
    { path: "/admin", label: "Dashboard", icon: <DashboardIcon /> },
    { path: "/admin/companies", label: "Empresas", icon: <BusinessIcon /> },
    { path: "/admin/reports", label: "Relatórios Financeiros", icon: <BarChartIcon /> },
    { path: "/admin/sales-support", label: "Vendedores e Suporte", icon: <PersonIcon /> },
  ];

  const drawer = (
    <Box>
      {/* Logo */}
      <Toolbar
        disableGutters
        sx={{
          px: 2.5,
          minHeight: 72,
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          borderBottom: "1px solid rgba(255,255,255,0.08)",
        }}
      >
        <ButtonBase
          onClick={() => {
            navigate("/");
            if (isMobile) setMobileOpen(false);
          }}
          aria-label="Ir para o início"
          sx={{
            width: "-webkit-fill-available",
            textAlign: "center",
            borderRadius: 1,
            py: 0.5,
            px: 1.5,
            bgcolor: "#fcfbfc",
            justifyContent: "center",
            transition: "transform 0.18s ease, box-shadow 0.18s ease",
            "&:hover": {
              transform: "translateY(-1px)",
              boxShadow: "0 10px 24px rgba(3,20,38,0.22)",
            },
            "&:focus-visible": {
              outline: `2px solid ${brand.primaryLight}`,
              outlineOffset: 3,
            },
          }}
        >
          <Logo variant="full" size={44} />
        </ButtonBase>
      </Toolbar>

      {/* Admin Menu */}
      <List sx={{ px: 1, py: 2 }}>
        <ListItem disablePadding sx={{ mb: 0.5 }}>
          <Typography
            variant="caption"
            sx={{
              px: 2,
              py: 1,
              fontWeight: 700,
              letterSpacing: '0.5px',
              color: "rgba(226,238,249,0.58)",
            }}
          >
            ADMINISTRADOR
          </Typography>
        </ListItem>

        {adminMenuItems.map((item) => {
          const isActive = location.pathname === item.path;
          return (
            <ListItem key={item.path} disablePadding>
              <ListItemButton
                onClick={() => {
                  navigate(item.path);
                  if (isMobile) setMobileOpen(false);
                }}
                sx={{
                  borderRadius: 1,
                  mx: 0.5,
                  mb: 0.25,
                  bgcolor: isActive ? 'primary.main' : 'transparent',
                  background: isActive
                    ? `linear-gradient(135deg, ${brand.primaryLight} 0%, ${brand.primary} 48%, ${brand.primaryDark} 100%)`
                    : "transparent",
                  color: isActive ? 'white' : 'rgba(226,238,249,0.82)',
                  boxShadow: isActive ? "0 12px 28px rgba(16,151,230,0.24)" : "none",
                  '&:hover': {
                    bgcolor: isActive ? 'primary.dark' : 'rgba(85,199,255,0.10)',
                    color: "white",
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    color: isActive ? 'white' : brand.primaryLight,
                    minWidth: 40,
                    width: 30,
                    height: 30,
                    borderRadius: 1,
                    mr: 1,
                    alignItems: "center",
                    justifyContent: "center",
                    bgcolor: isActive ? "rgba(255,255,255,0.18)" : "rgba(85,199,255,0.08)",
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                <ListItemText
                  primary={item.label}
                  primaryTypographyProps={{
                    fontSize: '0.9rem',
                    fontWeight: isActive ? 700 : 500,
                  }}
                />
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>

      <Divider sx={{ my: 2 }} />

      {/* Configurações */}
      <List sx={{ px: 1 }}>
        <ListItem disablePadding sx={{ mb: 0.5 }}>
          <Typography
            variant="caption"
            sx={{
              px: 2,
              py: 1,
              fontWeight: 700,
              letterSpacing: '0.5px',
              color: "rgba(226,238,249,0.58)",
            }}
          >
            CONFIGURAÇÕES
          </Typography>
        </ListItem>

        <ListItem disablePadding>
          <ListItemButton
            onClick={() => {
              navigate('/admin/settings');
              if (isMobile) setMobileOpen(false);
            }}
            sx={{
              borderRadius: 1,
              mx: 0.5,
              mb: 0.25,
              color: "rgba(226,238,249,0.82)",
              "&:hover": {
                bgcolor: "rgba(85,199,255,0.10)",
                color: "white",
              },
            }}
          >
            <ListItemIcon
              sx={{
                color: brand.primaryLight,
                minWidth: 40,
                width: 30,
                height: 30,
                borderRadius: 1,
                mr: 1,
                alignItems: "center",
                justifyContent: "center",
                bgcolor: "rgba(85,199,255,0.08)",
              }}
            >
              <SettingsIcon />
            </ListItemIcon>
            <ListItemText
              primary={"Configurações do Sistema"}
              primaryTypographyProps={{
                fontSize: '0.9rem',
              }}
            />
          </ListItemButton>
        </ListItem>
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      {/* AppBar */}
      <AppBar
        position="fixed"
        sx={{
          width: { md: `calc(100% - ${drawerWidth}px)` },
          ml: { md: `${drawerWidth}px` },
          bgcolor: 'rgba(255,255,255,0.88)',
          backdropFilter: "blur(14px)",
          color: 'text.primary',
          boxShadow: 'none',
          borderBottom: `1px solid ${brand.surface}`,
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { md: 'none' } }}
          >
            <MenuIcon />
          </IconButton>

          <Box sx={{ flexGrow: 1 }} />

          {/* User Menu */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ display: { xs: 'none', sm: 'block' }, textAlign: 'right' }}>
              <Typography variant="body2" fontWeight="600">
                {user?.name}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Administrador do Sistema
              </Typography>
            </Box>
            <IconButton onClick={handleMenuOpen} size="small">
              <Avatar
                sx={{
                  width: 40,
                  height: 40,
                  bgcolor: 'primary.main',
                  background: `linear-gradient(135deg, ${brand.primaryLight}, ${brand.primaryDark})`,
                }}
              >
                {user?.name?.charAt(0).toUpperCase()}
              </Avatar>
            </IconButton>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
            >
              <MenuItem disabled>
                <Typography variant="body2" color="text.secondary">
                  {user?.email}
                </Typography>
              </MenuItem>
              <Divider />
              <MenuItem onClick={handleLogout}>
                <ListItemIcon>
                  <LogoutIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Sair</ListItemText>
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Drawer */}
      <Box
        component="nav"
        sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              bgcolor: brand.navy,
              color: "white",
            },
          }}
        >
          {drawer}
        </Drawer>

        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              bgcolor: brand.navy,
              color: "white",
              borderRight: "1px solid rgba(85,199,255,0.16)",
              boxShadow: "14px 0 34px rgba(3,20,38,0.13)",
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { md: `calc(100% - ${drawerWidth}px)` },
          minHeight: '100vh',
          bgcolor: 'background.default',
          background:
            "radial-gradient(900px 360px at 88% -120px, rgba(16,151,230,0.11), transparent 60%), linear-gradient(180deg, #f8fbff 0%, #eef5fb 100%)",
        }}
      >
        <Toolbar />
        {children}
      </Box>
    </Box>
  );
}
